import { Injectable } from '@angular/core';
import { set } from 'idb-keyval';
import { get } from 'idb-keyval';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  private readonly defaultBackendPublicKey = "MCowBQYDK2VuAyEAgN+Y9OCr1K40SZIUpBJ6/Y0V5yZYgMBT0Fh5ZgMbvxs=";
  private nobleX25519Module: any = null;

  constructor() { }





  async initiateEncryption(): Promise<boolean> {
    try {
      if (!window.isSecureContext) {
        console.error("Error in initiateEncryption: WebCrypto requires a secure context (HTTPS or localhost).");
        return false;
      }

      if (!window.crypto || !window.crypto.subtle) {
        console.error("Error in initiateEncryption: SubtleCrypto is unavailable in this browser/runtime.");
        return false;
      }

      const selectedAlgorithm = await this.selectSupportedAlgorithm();
      console.log("Selected key agreement algorithm:", selectedAlgorithm);
      if (!selectedAlgorithm) {
        console.error("Error in initiateEncryption: Neither X25519 nor ECDH P-256 is supported in this browser/runtime.");
        return false;
      }
      sessionStorage.setItem("frontendKeyAlgorithm", selectedAlgorithm);

      //create a new key pair
      const frontendKeys = await this.generateKeyPair(selectedAlgorithm);
      console.log("Frontend Public Key", frontendKeys);
      // export the public key and store it in session storage
      const frontendPublicKeyBase64 = await this.exportPublicKey(frontendKeys.publicKey);
      sessionStorage.setItem("frontendPublicKey", frontendPublicKeyBase64);
      localStorage.setItem("frontendPublicKey", frontendPublicKeyBase64);
      //export the private key and store it in session storage
      // const frontendPrivateKeyBase64 = await this.exportPrivateKey(frontendKeys.privateKey);
      // console.log("Frontend Private Key", frontendPrivateKeyBase64);
      // sessionStorage.setItem("frontendPrivateKey", frontendPrivateKeyBase64);
      await this.setKey('frontendPrivateKey', frontendKeys.privateKey);
      //import pre shared backend public key
      const backendPublicKeyString = sessionStorage.getItem("backendPublicKey") || this.defaultBackendPublicKey;
      const backendPublicKey = await this.importPublicKey(backendPublicKeyString);
      if (!backendPublicKey) {
        console.error("Error in initiateEncryption: backend public key import failed.");
        return false;
      }
      // Derive shared secret using frontend private key and backend public key
      const sharedSecret = await this.deriveSharedSecret(frontendKeys.privateKey, backendPublicKey);
      // Derive AES key from shared secret
      await this.deriveAESKey(sharedSecret);
      return true;
    } catch (e) {
      console.error("Error in initiateEncryption", e);
      return false;
    }



  }

  private async isX25519Supported(): Promise<boolean> {
    try {
      const testKeys = await crypto.subtle.generateKey(
        { name: "X25519" },
        false,
        ["deriveBits"]
      );
      return !!testKeys;
    } catch {
      return false;
    }
  }

  private async isP256Supported(): Promise<boolean> {
    try {
      const testKeys = await crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" },
        false,
        ["deriveBits"]
      );
      return !!testKeys;
    } catch {
      return false;
    }
  }

  private async selectSupportedAlgorithm(): Promise<"X25519" | "ECDH-P256" | "X25519-NOBLE" | null> {
    if (await this.isX25519Supported()) {
      return "X25519";
    }
    if (await this.isP256Supported()) {
      return "ECDH-P256";
    }
    if (await this.isNobleX25519Supported()) {
      return "X25519-NOBLE";
    }
    return null;
  }

  private async loadNobleX25519() {
    if (this.nobleX25519Module) {
      return this.nobleX25519Module;
    }
    const mod: any = await import("@noble/curves/ed25519");
    this.nobleX25519Module = mod?.x25519;
    return this.nobleX25519Module;
  }

  private async isNobleX25519Supported(): Promise<boolean> {
    try {
      const x25519 = await this.loadNobleX25519();
      return !!x25519?.getPublicKey && !!x25519?.getSharedSecret && !!x25519?.utils;
    } catch {
      return false;
    }
  }

  // async setKey(keyName: string, value: any): Promise<void> {
  //   try {
  //     await set(keyName, value);
  //     console.log(`Stored "${keyName}" in IndexedDB.`);
  //   } catch (error) {
  //     console.error(`Failed to store "${keyName}" in IndexedDB:`, error);
  //     localStorage.setItem(keyName, value);
  //   }
  // }

  // async getKey<T = any>(keyName: string): Promise<T | null> {
  //   try {
  //     const value = await get(keyName);
  //     if (value === undefined) {
  //       console.warn(`No value found in IndexedDB for key "${keyName}"`);
  //       return null;
  //     }
  //     return value as T;
  //   } catch (error) {
  //     console.error(`Failed to get "${keyName}" from IndexedDB:`, error);

  //     return localStorage.getItem(keyName) as T || null;
  //   }
  // }

  // Add this helper in the same service
  private isCryptoKey(obj: any): obj is CryptoKey {
    // Best-effort detection for CryptoKey shape
    return !!obj && typeof obj === 'object' && typeof obj.type === 'string' && !!obj.algorithm;
  }

  private isUint8Array(obj: any): obj is Uint8Array {
    return obj instanceof Uint8Array;
  }

  /**
   * Store a value in localStorage. Handles CryptoKey (exports to base64) and ordinary JS values.
   * Keeps the API async so your existing calls don't need to change.
   */
  async setKey(keyName: string, value: any): Promise<void> {
    try {
      // CryptoKey -> export + store as JSON descriptor
      if (this.isCryptoKey(value)) {
        // private vs public
        if (value.type === 'private') {
          const exported = await this.exportPrivateKey(value); // pkcs8 base64
          const payload = JSON.stringify({
            __type: 'crypto-key',
            keyType: 'private',
            format: 'pkcs8',
            value: exported
          });
          localStorage.setItem(keyName, payload);
          console.log(`Stored private CryptoKey "${keyName}" in localStorage (exported PKCS8).`);
          return;
        } else if (value.type === 'public') {
          const exported = await this.exportPublicKey(value); // spki base64
          const payload = JSON.stringify({
            __type: 'crypto-key',
            keyType: 'public',
            format: 'spki',
            value: exported
          });
          localStorage.setItem(keyName, payload);
          console.log(`Stored public CryptoKey "${keyName}" in localStorage (exported SPKI).`);
          return;
        }
      }

      if (this.isUint8Array(value)) {
        const payload = JSON.stringify({
          __type: 'uint8array',
          value: btoa(String.fromCharCode(...value))
        });
        localStorage.setItem(keyName, payload);
        return;
      }

      // Non-CryptoKey -> JSON stringify (safe for objects, arrays, primitives)
      localStorage.setItem(keyName, JSON.stringify({ __type: 'json', value }));
      console.log(`Stored "${keyName}" in localStorage.`);
    } catch (error) {
      console.error(`Failed to store "${keyName}" in localStorage:`, error);
    }
  }

  /**
   * Retrieve the key. If a CryptoKey was stored, it will import and return a CryptoKey.
   * Returns null if missing.
   */
  async getKey<T = any>(keyName: string): Promise<T | CryptoKey | Uint8Array | null> {
    try {
      const raw = localStorage.getItem(keyName);
      if (raw === null) {
        console.warn(`No value found in localStorage for key "${keyName}"`);
        return null;
      }

      // try parse
      let parsed: any;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // not JSON — legacy or corrupted; return raw string
        return raw as unknown as T;
      }

      if (parsed && parsed.__type === 'crypto-key') {
        // crypto key descriptor
        if (parsed.keyType === 'private') {
          const imported = await this.importPrivateKey(parsed.value);
          return imported as CryptoKey;
        } else if (parsed.keyType === 'public') {
          const imported = await this.importPublicKey(parsed.value);
          return imported as CryptoKey;
        } else {
          console.warn("Unknown crypto keyType in storage:", parsed.keyType);
          return null;
        }
      }

      if (parsed && parsed.__type === 'uint8array') {
        return Uint8Array.from(atob(parsed.value), c => c.charCodeAt(0));
      }

      if (parsed && parsed.__type === 'json') {
        return parsed.value as T;
      }

      // fallback: return parsed directly
      return parsed as T;
    } catch (error) {
      console.error(`Failed to get "${keyName}" from localStorage:`, error);
      return null;
    }
  }

  // Optional: convenience removers
  async removeKey(keyName: string) {
    localStorage.removeItem(keyName);
  }
  async clearAllKeys() {
    localStorage.clear();
  }


  private getSelectedAlgorithm(): "X25519" | "ECDH-P256" | "X25519-NOBLE" {
    const algorithm = sessionStorage.getItem("frontendKeyAlgorithm");
    if (algorithm === "ECDH-P256") {
      return "ECDH-P256";
    }
    if (algorithm === "X25519-NOBLE") {
      return "X25519-NOBLE";
    }
    return "X25519";
  }

  async generateKeyPair(algorithm: "X25519" | "ECDH-P256" | "X25519-NOBLE"): Promise<CryptoKeyPair | { privateKey: Uint8Array; publicKey: Uint8Array }> {
    if (algorithm === "X25519-NOBLE") {
      const x25519 = await this.loadNobleX25519();
      const privateKey = (x25519?.utils?.randomPrivateKey?.() || x25519?.utils?.randomSecretKey?.()) as Uint8Array;
      if (!privateKey) {
        throw new Error("Noble X25519 random private key generator not available.");
      }
      const publicKey = x25519.getPublicKey(privateKey) as Uint8Array;
      return { privateKey, publicKey };
    }

    const keyOptions = algorithm === "ECDH-P256"
      ? { name: "ECDH", namedCurve: "P-256" as const }
      : { name: "X25519" as const };

      console.log("Generating key pair with algorithm:", keyOptions);

    const keyPair = await crypto.subtle.generateKey(
      keyOptions,
      true,
      ["deriveKey", "deriveBits"]
    );

    return keyPair as CryptoKeyPair;
  }

  async exportPublicKey(key: CryptoKey | Uint8Array) {
    if (key instanceof Uint8Array) {
      return btoa(String.fromCharCode(...key));
    }
    const exported = await crypto.subtle.exportKey("spki", key);
    return btoa(String.fromCharCode(...new Uint8Array(exported)));
  }

  async exportPrivateKey(privateKey: CryptoKey | Uint8Array): Promise<string> {
    if (privateKey instanceof Uint8Array) {
      return btoa(String.fromCharCode(...privateKey));
    }
    const exported = await crypto.subtle.exportKey('pkcs8', privateKey);
    return btoa(String.fromCharCode(...new Uint8Array(exported)));
  }


  async deriveSharedSecret(privateKey, publicKey) {
    const algorithm = this.getSelectedAlgorithm();
    if (algorithm === "X25519-NOBLE") {
      const x25519 = await this.loadNobleX25519();
      const shared = x25519.getSharedSecret(privateKey, publicKey) as Uint8Array;
      return shared.slice(0, 32).buffer;
    }
    return await crypto.subtle.deriveBits(
      algorithm === "ECDH-P256"
        ? {
          name: "ECDH",
          public: publicKey
        }
        : {
          name: "X25519",
          public: publicKey
        },
      privateKey,
      256
    );
  }

  async deriveAESKey(sharedSecret) {
    return await crypto.subtle.importKey(
      "raw",
      await crypto.subtle.digest("SHA-256", sharedSecret),
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  isValidBase64(str: string): boolean {
    try {
      return btoa(atob(str)) === str.replace(/\s+/g, '');
    } catch {
      return false;
    }
  }

  async encryptAES(key, plaintext) {
    let base64text = JSON.stringify(plaintext)
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV for AES-GCM
    const encoder = new TextEncoder();
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv,
        tagLength: 128 // 16-byte auth tag
      },
      key,
      encoder.encode(base64text)
    );

    // Concatenate IV and ciphertext
    const encryptedBytes = new Uint8Array(encryptedData);
    const finalData = new Uint8Array(iv?.length + encryptedBytes?.length);

    finalData.set(iv, 0); // Set IV at the beginning
    finalData.set(encryptedBytes, iv?.length); // Append ciphertext after IV

    return btoa(String.fromCharCode.apply(null, finalData)); // Convert to Base64
  }

  async decryptAES(key, encryptedBase64) {
    // console.log("Decrypting AES with key:", encryptedBase64);

    if (!/^[A-Za-z0-9+/=]+$/.test(encryptedBase64) || !encryptedBase64 || typeof encryptedBase64 !== 'string' || !this.isValidBase64(encryptedBase64)) {
      throw new Error("❌ decryptAES: Input is not valid base64");
    }
    // const encryptedData = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));


    const cleanBase64 = encryptedBase64?.replace(/\s+/g, '');
    const encryptedData = Uint8Array.from(atob(cleanBase64), c => c.charCodeAt(0));



    const iv = encryptedData.slice(0, 12); // First 12 bytes = IV
    const ciphertext = encryptedData.slice(12); // Rest is ciphertext + authTag

    const decryptedData = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
        tagLength: 128
      },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decryptedData);
  }







  async importPublicKey(base64Key) {
    try {
      // Convert Base64 to raw bytes
      const rawKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
      const algorithm = this.getSelectedAlgorithm();
      if (algorithm === "X25519-NOBLE") {
        return rawKey;
      }

      // Import the public key
      return await crypto.subtle.importKey(
        "spki",          // Raw public key format
        rawKey,         // Key data
        algorithm === "ECDH-P256"
          ? { name: "ECDH", namedCurve: "P-256" }
          : { name: "X25519" },
        true,           // Extractable (allows exporting again if needed)
        []              // No key usages (only for key derivation)
      );
    } catch (error) {
      console.error("Error importing public key:", error);
      return null;
    }
  }

  async importPrivateKey(base64Key) {
    try {
      const rawKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
      const algorithm = this.getSelectedAlgorithm();
      if (algorithm === "X25519-NOBLE") {
        return rawKey;
      }

      return await crypto.subtle.importKey(
        "pkcs8",
        rawKey,
        algorithm === "ECDH-P256"
          ? { name: "ECDH", namedCurve: "P-256" }
          : { name: "X25519" },
        true,
        ["deriveKey", "deriveBits"]
      );
    } catch (error) {
      console.error("Error importing private key:", error);
      return null;
    }
  }
}



